import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { getAdminMe, type AdminMe } from '@/api/admin';
import { AdminApiError } from '@/api/client';
import { signIn, signOut } from './authService';

type AuthState =
  | { state: 'loading' }
  | { state: 'signed_out' }
  | { state: 'not_authorized' }
  | { state: 'error'; message: string }
  | { state: 'authorized'; me: AdminMe };

interface AuthValue { status: AuthState; signIn: () => Promise<void>; signOut: () => void; retry: () => void }
const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    void getAdminMe(controller.signal)
      .then(me => setStatus({ state: 'authorized', me }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof AdminApiError && error.kind === 'signed_out') setStatus({ state: 'signed_out' });
        else if (error instanceof AdminApiError && error.kind === 'forbidden') setStatus({ state: 'not_authorized' });
        else setStatus({ state: 'error', message: error instanceof Error ? error.message : 'Unknown error' });
      });
    return () => controller.abort();
  }, [attempt]);

  return <AuthContext.Provider value={{ status, signIn: () => signIn('/'), signOut, retry: () => { setStatus({ state: 'loading' }); setAttempt(value => value + 1); } }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
